import { Avatar, Box, Button, Divider, Flex, HStack, Icon, IconButton, Text, useToast, VStack } from "@chakra-ui/react";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  PiChartBarDuotone, PiCalendarBlankDuotone, PiCaretRightBold, PiArrowBendUpLeftDuotone, PiFlagDuotone,
  PiHeartDuotone, PiHeartFill, PiChatCircleDuotone, PiPaperPlaneRightFill, PiTrashDuotone, PiUsersThreeDuotone,
  PiPushPinDuotone, PiPushPinFill
} from "react-icons/pi";
import { GLASS } from "../../components/glassUi";
import {
  createPortalComment,
  createWallComment,
  deletePortalComment,
  deletePortalPost,
  deleteWallComment,
  deleteWallPost,
  joinDiscoverGroup,
  pinWallPost,
  reactPortalPost,
  reactWallPost,
  htmlToPlainText,
  WallComment,
  WallPost
} from "@lubbezposrednio-frontend/core";
import { MentionTextarea, mediaUrl, relTime, RichText } from "./wallShared";
import { VerifiedBadge, AvatarVerifiedTick } from "../../components/VerifiedBadge";
import ReportPopup from "../VotingView/components/ReportPopup/ReportPopup";

const profileLink = (ref?: string | null) => (ref ? `/profil/${ref}` : undefined);

const VSTATUS: Record<string, { label: string; color: string; bg: string }> = {
  ongoing: { label: "Trwa", color: "#1f9e18", bg: "rgba(47,206,37,0.12)" },
  planned: { label: "Zaplanowane", color: "#430098", bg: "rgba(67,0,152,0.1)" },
  finished: { label: "Zakończone", color: "#64748b", bg: "rgba(100,116,139,0.12)" },
  draft: { label: "Szkic", color: "#b45309", bg: "rgba(245,158,11,0.12)" }
};

const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString("pl-PL", { day: "numeric", month: "short", year: "numeric" }) : "");

const OUTCOME_LABEL = (o?: { kind: string; winner?: string } | null): { text: string; color: string } | null => {
  if (!o) return null;
  switch (o.kind) {
    case "passed": return { text: "Przyjęto", color: "#1f9e18" };
    case "rejected": return { text: "Odrzucono", color: "#e51f44" };
    case "winner": return { text: `Zwycięzca: ${o.winner ?? ""}`, color: "#1f9e18" };
    case "passed_options": return { text: "Przyjęto opcje", color: "#1f9e18" };
    case "ranking": return { text: "Ranking", color: "#430098" };
    case "no_decision": return { text: "Brak rozstrzygnięcia", color: "#b45309" };
    default: return null;
  }
};

// Mini-wykres: poziome paski odpowiedzi z procentami (po zakończeniu, dla uprawnionych).
const ResultsMini: React.FC<{ r: NonNullable<NonNullable<WallPost["voting"]>["results"]> }> = ({ r }) => {
  const shownQuestions = r.questions.slice(0, 2);
  const moreQ = r.questions.length - shownQuestions.length;
  return (
    <Box px="16px" py="13px" borderTop="1px solid rgba(67,0,152,0.08)" bg="white">
      <HStack spacing="14px" mb="11px" fontSize="12.5px" wrap="wrap">
        <HStack spacing="5px"><Text color="gray.400">Frekwencja</Text><Text fontWeight="800" color="primary">{r.turnout_percent ?? 0}%</Text></HStack>
        <HStack spacing="5px"><Text color="gray.400">Głosów</Text><Text fontWeight="800" color="primary">{r.voted_count}/{r.eligible_count}</Text></HStack>
        <Box bg={r.valid ? "rgba(47,206,37,0.12)" : "rgba(245,158,11,0.12)"} color={r.valid ? "#1f9e18" : "#b45309"} px="9px" py="2px" borderRadius="full" fontSize="11px" fontWeight="800">
          {r.valid ? "Ważne" : "Nieważne (kworum)"}
        </Box>
      </HStack>

      <VStack align="stretch" spacing="12px">
        {shownQuestions.map((q, qi) => {
          const ol = OUTCOME_LABEL(q.outcome);
          const max = Math.max(1, ...q.answers.map(a => a.percent));
          return (
            <Box key={qi}>
              <HStack justify="space-between" mb="6px" align="flex-start">
                <Text fontSize="13px" fontWeight="700" color="gray.700" noOfLines={1}>{htmlToPlainText(q.content)}</Text>
                {ol && <Text fontSize="11px" fontWeight="800" color={ol.color} flexShrink={0}>{ol.text}</Text>}
              </HStack>
              <VStack align="stretch" spacing="5px">
                {q.answers.slice(0, 4).map((a, ai) => (
                  <Box key={ai}>
                    <HStack justify="space-between" mb="2px">
                      <Text fontSize="12px" color="gray.600" noOfLines={1}>{htmlToPlainText(a.content)}</Text>
                      <Text fontSize="12px" fontWeight="700" color="primary" flexShrink={0}>{a.percent}%</Text>
                    </HStack>
                    <Box bg="rgba(67,0,152,0.08)" borderRadius="full" h="7px" overflow="hidden">
                      <Box h="100%" borderRadius="full" w={`${a.percent}%`} minW={a.count > 0 ? "5px" : "0"} bgGradient={a.percent === max ? "linear(90deg, #430098, #7b2ff7)" : "linear(90deg, rgba(67,0,152,0.5), rgba(123,47,247,0.5))"} />
                    </Box>
                  </Box>
                ))}
              </VStack>
            </Box>
          );
        })}
      </VStack>
      {moreQ > 0 && <Text fontSize="11.5px" color="gray.400" mt="9px">+ {moreQ} {moreQ === 1 ? "pytanie" : "pytania"} więcej</Text>}
    </Box>
  );
};

// Osadzona karta-preview głosowania na ścianie (jak preview linku na FB). Po zakończeniu + wykresy wyników.
const VotingPreviewCard: React.FC<{ v: NonNullable<WallPost["voting"]> }> = ({ v }) => {
  const s = VSTATUS[v.status] || { label: v.status, color: "#64748b", bg: "rgba(100,116,139,0.12)" };
  const active = v.status === "ongoing";
  const finished = v.status === "finished";
  return (
    <Box as={Link} to={`/glosowanie/${v.id}`} display="block" mt="12px" borderRadius="14px" border="1px solid rgba(67,0,152,0.14)" overflow="hidden" transition="all 0.12s" _hover={{ boxShadow: "0 8px 22px rgba(67,0,152,0.1)", borderColor: "rgba(67,0,152,0.3)" }} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
      <Box bgGradient="linear(120deg, rgba(67,0,152,0.06), rgba(123,47,247,0.05))" px="16px" py="13px">
        <HStack spacing="8px" mb="7px">
          <Icon as={PiChartBarDuotone} color="primary" boxSize="15px" />
          <Text fontSize="11px" fontWeight="800" textTransform="uppercase" letterSpacing="0.05em" color="primary">Głosowanie</Text>
          <Box bg={s.bg} color={s.color} px="9px" py="2px" borderRadius="full" fontSize="11px" fontWeight="800">{s.label}</Box>
        </HStack>
        <Text fontWeight="800" color="primary" fontSize="16px" noOfLines={2}>{v.title}</Text>
        <HStack spacing="14px" mt="9px" color="gray.500" fontSize="12.5px" wrap="wrap">
          {(v.start_date || v.end_date) && (
            <HStack spacing="5px"><Icon as={PiCalendarBlankDuotone} boxSize="13px" /><Text>{fmtDate(v.start_date)}{v.end_date ? ` – ${fmtDate(v.end_date)}` : ""}</Text></HStack>
          )}
          {typeof v.voters_count === "number" && v.voters_count > 0 && (
            <HStack spacing="5px"><Icon as={PiUsersThreeDuotone} boxSize="13px" /><Text>{v.voters_count} uprawnionych</Text></HStack>
          )}
        </HStack>
      </Box>

      {finished && v.results && <ResultsMini r={v.results} />}
      {finished && !v.results && v.results_locked && (
        <Box px="16px" py="11px" borderTop="1px solid rgba(67,0,152,0.08)" bg="white">
          <Text fontSize="12.5px" color="gray.500">🔒 Wyniki widoczne tylko dla uprawnionych.</Text>
        </Box>
      )}

      <Flex align="center" justify="space-between" px="16px" py="10px" borderTop="1px solid rgba(67,0,152,0.08)" bg="white">
        <Text fontSize="13px" fontWeight="700" color={active ? "#1f9e18" : "primary"}>{active ? "Zagłosuj teraz" : finished ? "Zobacz pełne wyniki" : "Zobacz głosowanie"}</Text>
        <Icon as={PiCaretRightBold} color="gray.400" boxSize="13px" />
      </Flex>
    </Box>
  );
};

const AuthorLink: React.FC<{ author: WallComment["author"]; fontSize?: string }> = ({ author, fontSize = "13px" }) => {
  const to = profileLink(author.profile_ref);
  const name = to ? (
    <Text as={Link} to={to} fontWeight="700" color="primary" fontSize={fontSize} noOfLines={1} _hover={{ textDecoration: "underline" }}>{author.name}</Text>
  ) : (
    <Text fontWeight="700" color="primary" fontSize={fontSize} noOfLines={1}>{author.name}</Text>
  );
  return (
    <HStack spacing="4px" minW={0}>
      {name}
      {author.verified && <VerifiedBadge size="13px" />}
    </HStack>
  );
};

const CommentAvatar: React.FC<{ author: WallComment["author"]; size?: string }> = ({ author, size = "30px" }) => {
  const av = (
    <Box position="relative" display="inline-flex" flexShrink={0}>
      <Avatar size="xs" boxSize={size} name={author.name} src={mediaUrl(author.avatar_url)} bg="rgba(67,0,152,0.1)" color="primary" />
      {author.verified && <AvatarVerifiedTick size="13px" />}
    </Box>
  );
  const to = profileLink(author.profile_ref);
  return to ? <Link to={to}>{av}</Link> : av;
};

// Pojedynczy komentarz (z opcjonalnymi odpowiedziami — jeden poziom zagnieżdżenia).
const CommentView: React.FC<{
  comment: WallComment;
  groupId: string;
  postId: string;
  canModerate: boolean;
  isPortal?: boolean;
  isReply?: boolean;
  onReply?: (parentId: string, reply: WallComment) => void;
  onDelete: (commentId: string) => void;
}> = ({ comment, groupId, postId, canModerate, isPortal, isReply, onReply, onDelete }) => {
  const toast = useToast();
  const [replying, setReplying] = useState(false);
  const [draft, setDraft] = useState("");
  const [showReplies, setShowReplies] = useState(false);
  const [reporting, setReporting] = useState(false);
  const replies = comment.replies ?? [];

  const submitReply = async () => {
    const value = draft.trim();
    if (!value) return;
    try {
      const { data } = isPortal
        ? await createPortalComment(postId, value, comment.id)
        : await createWallComment(groupId, postId, value, comment.id);
      onReply?.(comment.id, data);
      setDraft("");
      setReplying(false);
      setShowReplies(true);
    } catch {
      toast({ title: "Nie udało się odpowiedzieć", status: "error", isClosable: true });
    }
  };

  const removeMine = async () => {
    if (!window.confirm("Usunąć ten komentarz?")) return;
    try {
      if (isPortal) await deletePortalComment(comment.id);
      else await deleteWallComment(groupId, comment.id);
      onDelete(comment.id);
    } catch {
      /* noop */
    }
  };

  return (
    <Flex gap="9px" align="flex-start">
      <CommentAvatar author={comment.author} size={isReply ? "26px" : "30px"} />
      <Box flex="1" minW={0}>
        <Box bg="#FAF8FE" borderRadius="12px" p="9px 13px">
          <HStack justify="space-between" align="center">
            <AuthorLink author={comment.author} />
            <HStack spacing="7px" flexShrink={0}>
              <Text fontSize="11px" color="gray.400">{relTime(comment.created_at)}</Text>
              {comment.can_report !== false && <Icon as={PiFlagDuotone} boxSize="12px" color="gray.300" cursor="pointer" _hover={{ color: "#e51f44" }} onClick={() => setReporting(true)} aria-label="Zgłoś" />}
              {comment.can_delete && <Icon as={PiTrashDuotone} boxSize="12px" color="gray.300" cursor="pointer" _hover={{ color: "#e51f44" }} onClick={removeMine} aria-label="Usuń" />}
            </HStack>
          </HStack>
          <Box mt="1px"><RichText fontSize="13.5px">{comment.body}</RichText></Box>
        </Box>

        {/* Akcje pod komentarzem-korzeniem */}
        {!isReply && (
          <HStack spacing="14px" mt="4px" pl="4px">
            <Text as="button" fontSize="12px" fontWeight="700" color="gray.500" _hover={{ color: "primary" }} onClick={() => setReplying(v => !v)}>
              Odpowiedz
            </Text>
            {replies.length > 0 && (
              <Text as="button" fontSize="12px" fontWeight="700" color="primary" onClick={() => setShowReplies(v => !v)}>
                {showReplies ? "Ukryj odpowiedzi" : `${replies.length} ${replies.length === 1 ? "odpowiedź" : "odpowiedzi"}`}
              </Text>
            )}
          </HStack>
        )}

        {/* Lista odpowiedzi */}
        {!isReply && showReplies && replies.length > 0 && (
          <VStack align="stretch" spacing="8px" mt="8px">
            {replies.map(r => (
              <CommentView key={r.id} comment={r} groupId={groupId} postId={postId} canModerate={canModerate} isPortal={isPortal} isReply onDelete={onDelete} />
            ))}
          </VStack>
        )}

        {/* Pole odpowiedzi */}
        {!isReply && replying && (
          <HStack mt="8px" spacing="8px" align="flex-end">
            <MentionTextarea
              groupId={groupId}
              value={draft}
              onChange={setDraft}
              onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitReply(); }
              }}
              placeholder={`Odpowiedz ${comment.author.name}…`}
              rows={1}
              minH="38px"
              resize="none"
              fontSize="13.5px"
              border="1px solid rgba(67,0,152,0.12)"
              borderRadius="11px"
              _focusVisible={{ borderColor: "primary", boxShadow: "0 0 0 1px var(--chakra-colors-primary)" }}
              maxLength={3000}
            />
            <IconButton aria-label="Wyślij" icon={<PiPaperPlaneRightFill />} onClick={submitReply} isDisabled={!draft.trim()} size="sm" borderRadius="10px" bg="primary" color="white" _hover={{ bg: "#360079" }} />
          </HStack>
        )}
        {reporting && <ReportPopup Id={comment.id} reportableType="comment" onClose={() => setReporting(false)} />}
      </Box>
    </Flex>
  );
};

const WallPostCard: React.FC<{
  post: WallPost;
  groupId: string;
  isModerator: boolean;
  onChange: (updater: (p: WallPost) => WallPost) => void;
  onRemove: () => void;
  groupContext?: { id: string; name: string; avatar_url?: string | null; is_public?: boolean; is_member?: boolean; requires_approval?: boolean };
  // false → post z grupy publicznej, do której widz NIE należy: read-only + „Dołącz, żeby odpowiadać".
  canInteract?: boolean;
}> = ({ post, groupId, isModerator, onChange, onRemove, groupContext, canInteract = true }) => {
  const toast = useToast();
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [joining, setJoining] = useState(false);
  const [requested, setRequested] = useState(false);
  const [reporting, setReporting] = useState(false);

  const joinGroup = async () => {
    if (!groupContext?.id) return;
    setJoining(true);
    try {
      const { data } = await joinDiscoverGroup(groupContext.id);
      if (data.status === "member") {
        toast({ title: "Dołączono do grupy ✓", status: "success", isClosable: true });
        onChange(x => x); // wymuś re-render; rodzic odświeży can_interact przy następnym ładowaniu
        setRequested(true);
      } else {
        setRequested(true);
        toast({ title: "Poproszono o dołączenie — czeka na akceptację", status: "info", isClosable: true });
      }
    } catch {
      toast({ title: "Nie udało się dołączyć", status: "error", isClosable: true });
    } finally {
      setJoining(false);
    }
  };

  const roots = post.comments;
  const COLLAPSED = 2;
  const visible = expanded ? roots : roots.slice(Math.max(0, roots.length - COLLAPSED));
  const hidden = roots.length - visible.length;

  const isPortal = post.kind === "portal";
  // Portal: reakcje dla każdego zalogowanego, komentarze wg polityki posta. Grupa: wszystko wg członkostwa.
  const canReact = isPortal ? true : canInteract;
  const canComment = isPortal ? post.can_comment !== false : canInteract;

  const toggleReact = async () => {
    onChange(x => ({ ...x, reacted_by_me: !x.reacted_by_me, reactions_count: x.reactions_count + (x.reacted_by_me ? -1 : 1) }));
    try {
      const { data } = isPortal ? await reactPortalPost(post.id) : await reactWallPost(groupId, post.id);
      onChange(x => ({ ...x, reacted_by_me: data.reacted, reactions_count: data.reactions_count }));
    } catch {
      /* noop */
    }
  };

  const togglePin = async () => {
    setBusy(true);
    try {
      const { data } = await pinWallPost(groupId, post.id);
      onChange(() => data);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Usunąć ten post?")) return;
    setBusy(true);
    try {
      if (isPortal) await deletePortalPost(post.id);
      else await deleteWallPost(groupId, post.id);
      onRemove();
    } finally {
      setBusy(false);
    }
  };

  const submitComment = async () => {
    const value = draft.trim();
    if (!value) return;
    try {
      const { data } = isPortal ? await createPortalComment(post.id, value) : await createWallComment(groupId, post.id, value);
      onChange(x => ({ ...x, comments: [...x.comments, data], comments_count: x.comments_count + 1 }));
      setDraft("");
      setExpanded(true);
    } catch {
      toast({ title: "Nie udało się dodać komentarza", status: "error", isClosable: true });
    }
  };

  const addReply = (parentId: string, reply: WallComment) =>
    onChange(x => ({
      ...x,
      comments_count: x.comments_count + 1,
      comments: x.comments.map(c => (c.id === parentId ? { ...c, replies: [...(c.replies ?? []), reply] } : c))
    }));

  const removeComment = (commentId: string) =>
    onChange(x => {
      const isRoot = x.comments.some(c => c.id === commentId);
      const removedReplies = isRoot ? (x.comments.find(c => c.id === commentId)?.replies?.length ?? 0) : 0;
      return {
        ...x,
        comments_count: x.comments_count - 1 - removedReplies,
        comments: x.comments
          .filter(c => c.id !== commentId)
          .map(c => ({ ...c, replies: (c.replies ?? []).filter(r => r.id !== commentId) }))
      };
    });

  return (
    <Box borderRadius="20px" p={{ base: "16px", md: "18px 22px" }} sx={GLASS}>
      {groupContext && (
        <HStack as={Link} to={`/organizator/grupy/${groupContext.id}`} spacing="8px" mb="11px" pb="11px" borderBottom="1px solid rgba(67,0,152,0.06)" _hover={{ "& p": { textDecoration: "underline" } }}>
          <Avatar size="xs" boxSize="24px" borderRadius="7px" name={groupContext.name} src={mediaUrl(groupContext.avatar_url)} bg="rgba(67,0,152,0.1)" color="primary" icon={<Icon as={PiUsersThreeDuotone} boxSize="12px" />} />
          <Text fontSize="13px" fontWeight="800" color="primary" noOfLines={1}>{groupContext.name}</Text>
        </HStack>
      )}
      {post.pinned && (
        <HStack spacing="6px" mb="10px" color="#b45309">
          <Icon as={PiPushPinFill} boxSize="14px" />
          <Text fontSize="11.5px" fontWeight="800" textTransform="uppercase" letterSpacing="0.05em">Przypięty</Text>
        </HStack>
      )}
      {post.topic && (
        <HStack spacing="6px" mb="10px" bg="rgba(67,0,152,0.06)" borderRadius="full" px="10px" py="4px" w="fit-content">
          <Icon as={PiChatCircleDuotone} boxSize="11px" color="primary" />
          <Text fontSize="11px" fontWeight="700" color="primary" noOfLines={1}>Temat: {post.topic.title}</Text>
        </HStack>
      )}
      <Flex justify="space-between" align="flex-start" gap="10px">
        <HStack spacing="11px" minW={0} align="flex-start">
          <CommentAvatar author={post.author} size="40px" />
          <Box minW={0}>
            <AuthorLink author={post.author} fontSize="14.5px" />
            <Text fontSize="12px" color="gray.400">{relTime(post.created_at)}</Text>
          </Box>
        </HStack>
        <HStack spacing="2px" flexShrink={0}>
          {isModerator && (
            <IconButton aria-label="Przypnij" icon={<Icon as={post.pinned ? PiPushPinFill : PiPushPinDuotone} />} onClick={togglePin} isLoading={busy} size="sm" variant="ghost" color={post.pinned ? "#b45309" : "gray.400"} />
          )}
          {post.can_report !== false && (
            <IconButton aria-label="Zgłoś" icon={<PiFlagDuotone />} onClick={() => setReporting(true)} size="sm" variant="ghost" color="gray.400" _hover={{ color: "#e51f44", bg: "rgba(229,31,68,0.06)" }} />
          )}
          {post.can_delete && (
            <IconButton aria-label="Usuń" icon={<PiTrashDuotone />} onClick={remove} isLoading={busy} size="sm" variant="ghost" color="gray.400" _hover={{ color: "#e51f44", bg: "rgba(229,31,68,0.06)" }} />
          )}
        </HStack>
      </Flex>
      {reporting && <ReportPopup Id={post.id} reportableType={isPortal ? "portal_post" : "post"} onClose={() => setReporting(false)} />}

      {post.body?.trim() && <Box mt="12px"><RichText>{post.body}</RichText></Box>}
      {post.voting && <VotingPreviewCard v={post.voting} />}

      <HStack spacing="18px" mt="14px" pt="12px" borderTop="1px solid rgba(67,0,152,0.06)">
        <HStack
          as="button"
          onClick={canReact ? toggleReact : joinGroup}
          spacing="6px"
          color={post.reacted_by_me ? "#e51f44" : "gray.500"}
          _hover={{ color: "#e51f44" }}
          transition="color 0.12s"
          cursor={canReact ? "pointer" : "not-allowed"}
          opacity={canReact ? 1 : 0.7}
        >
          <Icon as={post.reacted_by_me ? PiHeartFill : PiHeartDuotone} boxSize="18px" />
          <Text fontSize="13px" fontWeight="700">{post.reactions_count}</Text>
        </HStack>
        <HStack spacing="6px" color="gray.500">
          <Icon as={PiChatCircleDuotone} boxSize="17px" />
          <Text fontSize="13px" fontWeight="700">{post.comments_count}</Text>
        </HStack>
      </HStack>

      {roots.length > 0 && (
        <VStack align="stretch" spacing="10px" mt="13px">
          <Divider borderColor="rgba(67,0,152,0.06)" />
          {hidden > 0 && (
            <Text as="button" fontSize="13px" fontWeight="700" color="primary" textAlign="left" onClick={() => setExpanded(true)}>
              <Icon as={PiArrowBendUpLeftDuotone} boxSize="12px" mr="5px" />
              Pokaż wszystkie komentarze ({roots.length})
            </Text>
          )}
          {visible.map(c => (
            <CommentView key={c.id} comment={c} groupId={groupId} postId={post.id} canModerate={isModerator} isPortal={isPortal} onReply={addReply} onDelete={removeComment} />
          ))}
        </VStack>
      )}

      {canComment ? (
        <HStack mt="12px" spacing="9px" align="flex-end">
          <MentionTextarea
            groupId={isPortal ? "" : groupId}
            value={draft}
            onChange={setDraft}
            onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitComment(); }
            }}
            placeholder={isPortal ? "Napisz komentarz…" : "Napisz komentarz… (@ aby wspomnieć)"}
            rows={1}
            minH="40px"
            resize="none"
            fontSize="14px"
            border="1px solid rgba(67,0,152,0.12)"
            borderRadius="11px"
            _focusVisible={{ borderColor: "primary", boxShadow: "0 0 0 1px var(--chakra-colors-primary)" }}
            maxLength={3000}
          />
          <IconButton aria-label="Wyślij" icon={<PiPaperPlaneRightFill />} onClick={submitComment} isDisabled={!draft.trim()} size="md" borderRadius="11px" bg="primary" color="white" _hover={{ bg: "#360079" }} />
        </HStack>
      ) : isPortal ? (
        // Post portalowy z ograniczonym komentowaniem (policy: tylko zweryfikowani / wyłączone).
        <Flex mt="12px" align="center" gap="9px" bg="#FAF8FE" border="1px solid rgba(67,0,152,0.1)" borderRadius="12px" p="11px 14px">
          <Icon as={PiChatCircleDuotone} color="gray.400" boxSize="16px" />
          <Text fontSize="13px" color="gray.500">
            {post.comments_policy === "verified" ? "Komentować mogą tylko zweryfikowane konta." : "Komentowanie wyłączone."}
          </Text>
        </Flex>
      ) : (
        // Nie-członek grupy publicznej: tylko podgląd + zaproszenie do dołączenia (interakcje gate'owane na serwerze).
        <Flex mt="12px" align="center" justify="space-between" gap="10px" wrap="wrap" bg="#FAF8FE" border="1px solid rgba(67,0,152,0.1)" borderRadius="12px" p="11px 14px">
          <HStack spacing="9px" minW={0}>
            <Icon as={PiUsersThreeDuotone} color="primary" boxSize="17px" />
            <Text fontSize="13.5px" color="gray.600" noOfLines={2}>
              Dołącz do grupy <b>{groupContext?.name}</b>, żeby reagować i odpowiadać.
            </Text>
          </HStack>
          <Button
            size="sm"
            onClick={joinGroup}
            isLoading={joining}
            isDisabled={requested}
            bg="primary"
            color="white"
            borderRadius="10px"
            fontWeight="700"
            flexShrink={0}
            _hover={{ bg: "#360079" }}
          >
            {requested ? "Wysłano" : groupContext?.requires_approval ? "Poproś o dołączenie" : "Dołącz"}
          </Button>
        </Flex>
      )}
    </Box>
  );
};

export default WallPostCard;
